import { type AnyRouter, useRouter } from "@tanstack/react-router";
import * as React from "react";

/**
 * Where a fullscreen takeover (Settings / Integrations) should return to.
 *
 * Why this exists instead of ``router.history.back()``: the avatar drawer
 * navigates with ``replace: true`` (see ``UserMenuButton.go``) so a menu click
 * does not pollute browser history. That means the entry BEHIND a takeover is
 * not the section the user came from — it is whatever takeover they happened to
 * open before, typically ``/settings``. Popping the history therefore landed on
 * the Profile page and needed a second press to actually leave.
 *
 * So we remember the origin ourselves: every resolved navigation to a
 * ``/workspace/**`` URL is recorded, and the takeover shells navigate straight
 * back to it. Deterministic, one press, no intermediate page flashing by.
 *
 * ``sessionStorage`` (not just a module variable) so a reload while inside a
 * takeover still knows where to go back to; per-tab, cleared with the tab.
 */
const KEY = "ando:takeover-origin";

/** Any workspace surface is a valid origin; the chat tab is the safe default. */
const FALLBACK = "/workspace/conversations";

let cached: string | null = null;

function isWorkspaceHref(href: string): boolean {
  return href.startsWith("/workspace");
}

function remember(href: string): void {
  cached = href;
  try {
    window.sessionStorage.setItem(KEY, href);
  } catch {
    // Private mode / quota — the in-memory copy still covers this session.
  }
}

/** The recorded origin, or the chat tab when there is nothing usable. */
export function readTakeoverOrigin(): string {
  // sessionStorage is the source of truth (it survives a reload inside the
  // takeover); the in-memory copy only covers the case where storage throws.
  let stored: string | null;
  try {
    stored = window.sessionStorage.getItem(KEY) ?? cached;
  } catch {
    stored = cached;
  }
  // Guard the stored value: a stale or tampered entry must never send the user
  // outside the app (``navigate({ href })`` would happily follow it).
  return stored !== null && isWorkspaceHref(stored) ? stored : FALLBACK;
}

/**
 * Record every resolved ``/workspace/**`` navigation. Call once, next to
 * ``createRouter``. Returns the unsubscribe function.
 */
export function installTakeoverOriginTracker(router: AnyRouter): () => void {
  return router.subscribe("onResolved", ({ toLocation }) => {
    if (!isWorkspaceHref(toLocation.pathname)) return;
    remember(toLocation.href);
  });
}

/**
 * Back handler for a takeover shell: returns to the remembered workspace
 * surface, replacing the takeover entry so the browser's own back button does
 * not walk straight into it again.
 */
export function useTakeoverBack(): () => void {
  const router = useRouter();
  return React.useCallback((): void => {
    void router.navigate({ href: readTakeoverOrigin(), replace: true });
  }, [router]);
}
